import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVideos, uploadVideo, createExternalVideo, deleteVideo } from "../../api/videoApi";
import { getImages, uploadImage, createExternalImage, deleteImage } from "../../api/imageApi";
import { getPdfs, uploadPdf, createExternalPdf, deletePdf } from "../../api/pdfApi";

const AdminResearch = () => {
  const [media, setMedia] = useState({ videos: [], images: [], pdfs: [] });
  const [loading, setLoading] = useState(true);
  const [newMedia, setNewMedia] = useState({ title: "", url: "", file: null, isExternal: false });
  const category = "Research";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const videos = await getVideos(category);
        const images = await getImages(category);
        const pdfs = await getPdfs(category);
        setMedia({ videos, images, pdfs });
      } catch (error) {
        console.error("Error fetching media:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  const handleCreate = async (type) => {
    try {
      console.log(`🟢 handleCreate called - Type: ${type}`);

      let createdItem;

      if (newMedia.isExternal) {
        const data = { url: newMedia.url, title: newMedia.title, category };
        if (type === "videos") createdItem = await createExternalVideo(data);
        if (type === "images") createdItem = await createExternalImage(data);
        if (type === "pdfs") createdItem = await createExternalPdf(data);
      } else {
        if (!newMedia.file) {
          alert("Please select a file before uploading.");
          return;
        }

        const formData = new FormData();
        if (type === "pdfs") {
          formData.append("pdfFile", newMedia.file);
        } else {
          formData.append(type.slice(0, -1), newMedia.file);
        }
        formData.append("title", newMedia.title);
        formData.append("category", category);

        if (type === "videos") createdItem = await uploadVideo(formData, category);
        if (type === "images") createdItem = await uploadImage(formData, category);
        if (type === "pdfs") createdItem = await uploadPdf(formData, category);
      }

      if (createdItem) {
        setMedia((prev) => ({
          ...prev,
          [type]: [...prev[type], createdItem],
        }));

        if (type === "pdfs") {
          const updatedPdfs = await getPdfs(category);
          setMedia((prev) => ({
            ...prev,
            pdfs: updatedPdfs,
          }));
        }
      }
    } catch (error) {
      console.error("❌ Error creating media:", error);
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === "videos") await deleteVideo(id);
      if (type === "images") await deleteImage(id);
      if (type === "pdfs") await deletePdf(id);
      setMedia((prev) => ({ ...prev, [type]: prev[type].filter((item) => item._id !== id) }));
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container mx-auto p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate("/admin")}
        className="bg-gray-300 text-gray-800 px-4 py-2 rounded mb-4 hover:bg-gray-400"
      >
        ← Back to Dashboard
      </button>
      <h1 className="text-3xl font-bold mb-6">Admin - Research</h1>

      {/* Upload Media */}
      <div className="mb-6">
        <input type="text" placeholder="Title" value={newMedia.title} onChange={(e) => setNewMedia({ ...newMedia, title: e.target.value })} className="border rounded p-2 w-full mb-2" />
        <label className="mr-4">
          <input type="radio" name="uploadType" checked={!newMedia.isExternal} onChange={() => setNewMedia({ ...newMedia, isExternal: false, url: "" })} /> Upload File
        </label>
        <label>
          <input type="radio" name="uploadType" checked={newMedia.isExternal} onChange={() => setNewMedia({ ...newMedia, isExternal: true, file: null })} /> External URL
        </label>

        {newMedia.isExternal ? (
          <input type="text" placeholder="External URL" value={newMedia.url} onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value, file: null })} className="border rounded p-2 w-full mb-2" />
        ) : (
          <input type="file" onChange={(e) => setNewMedia((prev) => ({ ...prev, file: e.target.files[0] }))} className="border rounded p-2 w-full mb-2" />
        )}

        <div className="flex gap-2">
          <button onClick={() => handleCreate("videos")} className="bg-blue-500 text-white px-4 py-2 rounded">Add Video</button>
          <button onClick={() => handleCreate("images")} className="bg-green-500 text-white px-4 py-2 rounded">Add Image</button>
          <button onClick={() => handleCreate("pdfs")} className="bg-purple-500 text-white px-4 py-2 rounded">Add PDF</button>
        </div>
      </div>

      {/* Display Media */}
      {["videos", "images", "pdfs"].map((type) => (
        <div key={type} className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{type.charAt(0).toUpperCase() + type.slice(1)}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {media[type]?.map((item) => (
              <div key={item._id} className="border p-4 rounded shadow-md">
                <h3 className="text-lg font-semibold">{item.title}</h3>

                {type === "videos" && (
                  <video className="w-full mt-2 rounded" controls>
                    <source src={item.fileUrl ? `http://localhost:5555${item.fileUrl}` : item.url} type="video/mp4" />
                  </video>
                )}

                {type === "images" && (
                  <img className="w-full mt-2 rounded" src={item.fileUrl ? `http://localhost:5555${item.fileUrl}` : item.url} alt={item.title} />
                )}

                {type === "pdfs" && (
                  <button onClick={() => window.open(item.fileUrl || item.url, "_blank")} className="text-blue-500 hover:underline">
                    {item.title || "View PDF"}
                  </button>
                )}

                <button className="bg-red-500 text-white p-2 rounded mt-2" onClick={() => handleDelete(item._id, type)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminResearch;


